import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Loader2,
  PackagePlus,
  FilterX,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Activitat {
  id: number;
  name: string;
}

interface SupplierContact {
  name: string;
  position?: string;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
  tradeName?: string;
  cif: string;
  address?: string;
  city?: string;
  province?: string;
  phone?: string;
  email?: string;
  contacts?: SupplierContact[];
  qualityRating?: number;
  deliveryRating?: number;
  priceRating?: number;
  paymentTerms: number;
  supplierType: string;
  activitat?: Activitat | null;
}

const SUPPLIER_TYPE_CONFIG: Record<string, { label: string; cls: string }> = {
  HABITUAL:  { label: 'Habitual',  cls: 'bg-green-100 text-green-700' },
  ESPORADIC: { label: 'Esporàdic', cls: 'bg-blue-100 text-blue-700' },
  PUNTUAL:   { label: 'Puntual',   cls: 'bg-amber-100 text-amber-700' },
  OBSOLET:   { label: 'Obsolet',   cls: 'bg-slate-100 text-slate-500' },
};

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [activitats, setActivitats] = useState<Activitat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery]         = useSessionState('suppliers:search', '');
  const [activitatFilter, setActivitatFilter] = useSessionState('suppliers:activitatFilter', '');
  const [tipusFilter, setTipusFilter]         = useSessionState('suppliers:tipusFilter', 'HABITUAL');
  const [sortDir, setSortDir]                 = useSessionState<'ASC' | 'DESC'>('suppliers:sortDir', 'ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get<{ success: boolean; data: Activitat[] }>('/suppliers/activitats')
      .then((r) => setActivitats(r.data ?? []))
      .catch(() => {});
  }, []);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sortDir,
      });
      if (searchQuery)     params.append('search', searchQuery);
      if (activitatFilter) params.append('activitatId', activitatFilter);
      if (tipusFilter)     params.append('supplierType', tipusFilter);

      const response = await api.get<{
        success: boolean;
        data: Supplier[];
        pagination: { total: number; totalPages: number; page: number; limit: number };
      }>(`/suppliers?${params}`);

      setSuppliers(response.data);
      setTotalItems(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      console.error('[Suppliers] Error fetching suppliers:', err);
      setError('Error en carregar els proveïdors');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, activitatFilter, tipusFilter, sortDir]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSuppliers();
  };

  const toggleSort = () => {
    setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    setCurrentPage(1);
  };

  const confirmDelete = (supplier: Supplier) => {
    setSupplierToDelete(supplier);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!supplierToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/suppliers/${supplierToDelete.id}`);
      fetchSuppliers();
      setShowDeleteDialog(false);
      setSupplierToDelete(null);
    } catch (err) {
      console.error('[Suppliers] Error deleting supplier:', err);
    } finally {
      setDeleting(false);
    }
  };

  const getPrimaryContact = (supplier: Supplier): SupplierContact | null => {
    if (!supplier.contacts || supplier.contacts.length === 0) return null;
    return supplier.contacts.find(c => c.isPrimary) || supplier.contacts[0];
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-end">
        <Button
          type="button"
          variant="outline"
          title="Netejar tots els filtres"
          className="text-xs px-2.5"
          onClick={() => { setSearchQuery(''); setActivitatFilter(''); setTipusFilter('HABITUAL'); setCurrentPage(1); }}
        >
          <FilterX className="h-4 w-4" />
        </Button>

        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            autoFocus
            placeholder="Cercar per nom, CIF o codi..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-10 text-xs"
          />
        </div>

        {activitats.length > 0 && (
          <ExcelFilterDropdown
            label="Activitat"
            options={activitats.map((a) => ({ value: String(a.id), label: a.name }))}
            selected={activitatFilter ? activitatFilter.split(',') : []}
            onApply={(values) => { setActivitatFilter(values.join(',')); setCurrentPage(1); }}
          />
        )}

        <ExcelFilterDropdown
          label="Tipus"
          options={[
            { value: 'HABITUAL', label: 'Habitual' },
            { value: 'ESPORADIC', label: 'Esporàdic' },
            { value: 'PUNTUAL', label: 'Puntual' },
            { value: 'OBSOLET', label: 'Obsolet' },
          ]}
          selected={tipusFilter ? tipusFilter.split(',') : []}
          onApply={(values) => { setTipusFilter(values.join(',')); setCurrentPage(1); }}
        />

        <Button onClick={() => navigate('/compres/proveidors/new')} className="ml-auto text-xs">
          <Plus className="h-4 w-4 mr-1" />
          Nou Proveïdor
        </Button>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-xs">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Codi</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={toggleSort}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  Proveïdor
                  {sortDir === 'ASC' ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>
              </TableHead>
              <TableHead>Contacte</TableHead>
              <TableHead>Telèfon</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Estat</TableHead>
              <TableHead className="text-right">Accions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                </TableCell>
              </TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No hi ha proveïdors
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((supplier) => {
                const primaryContact = getPrimaryContact(supplier);
                const typeConfig = SUPPLIER_TYPE_CONFIG[supplier.supplierType] ?? SUPPLIER_TYPE_CONFIG.HABITUAL;

                return (
                  <TableRow
                    key={supplier.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/compres/proveidors/${supplier.id}`)}
                  >
                    <TableCell className="font-mono text-xs">{supplier.code}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium text-xs">{supplier.name}</div>
                        <div className="text-xs text-muted-foreground">{supplier.cif}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {primaryContact ? (
                        <div className="text-xs">
                          <div className="font-medium">{primaryContact.name}</div>
                          {primaryContact.position && (
                            <div className="text-muted-foreground">{primaryContact.position}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(primaryContact?.phone || supplier.phone) ? (
                        <div className="flex items-center gap-1 text-xs">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          {primaryContact?.phone || supplier.phone}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(primaryContact?.email || supplier.email) ? (
                        <a
                          href={`mailto:${primaryContact?.email || supplier.email}`}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[150px]">
                            {primaryContact?.email || supplier.email}
                          </span>
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium w-fit ${typeConfig.cls}`}>
                          {typeConfig.label}
                        </span>
                        {supplier.activitat && (
                          <span className="text-xs text-muted-foreground">{supplier.activitat.name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate('/compres/comandes/new', { state: { supplierId: supplier.id } })}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Nova comanda de compra"
                        >
                          <PackagePlus size={14} />
                        </button>
                        <button
                          onClick={() => navigate(`/compres/proveidors/${supplier.id}`)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => confirmDelete(supplier)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {totalItems} proveïdors trobats
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Mostrar:</span>
                {[25, 50, 100].map((size) => (
                  <button
                    key={size}
                    onClick={() => { setPageSize(size); setCurrentPage(1); }}
                    className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                      pageSize === size
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs">
                  Pàgina {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Proveïdor</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-xs">
            Estàs segur que vols eliminar el proveïdor <strong>{supplierToDelete?.name}</strong>?
            Aquesta acció no es pot desfer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel·lar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
